import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  FileText,
  Trash2,
  Copy,
  AlertCircle,
  CheckCircle,
  Loader,
  Download,
  FileSpreadsheet,
  RefreshCw,
} from "lucide-react";
import SEO from "./SEO";
import { supabase } from "../lib/supabase";

interface Upload {
  id: string;
  name: string;
  url: string;
  size: number;
  file_type: string;
  description?: string;
  created_at: string;
}

export default function AdminUploads() {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState<Upload | null>(
    null,
  );
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      setLoading(true);

      // Fetch uploads from database
      const { data, error } = await supabase
        .from("uploads")
        .select("*")
        .eq("deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (err) {
      console.error("Error fetching uploads:", err);
      setError("Failed to load uploads");
      setUploads([]);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      for (const file of acceptedFiles) {
        // Check file size
        if (file.size > 50 * 1024 * 1024) {
          // 50MB
          throw new Error(`File ${file.name} exceeds maximum size of 50MB`);
        }

        // Generate a unique filename but preserve the original name
        const originalName = file.name;
        const fileExt = originalName.split(".").pop();
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 15);
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = fileName;

        console.log(`Uploading file: ${originalName} as ${fileName}`);

        // Upload to storage
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from("uploads")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("Storage upload error:", uploadError);
          throw uploadError;
        }

        console.log("File uploaded successfully:", uploadData);

        // Get public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from("uploads").getPublicUrl(filePath);

        console.log("Public URL:", publicUrl);

        // Insert into database
        const { error: dbError, data: insertData } = await supabase
          .from("uploads")
          .insert([
            {
              name: originalName,
              url: publicUrl,
              size: file.size,
              file_type: file.type,
            },
          ])
          .select();

        if (dbError) {
          console.error("Database insert error:", dbError);
          throw dbError;
        }

        console.log("Database record created:", insertData);
      }

      await fetchUploads();
      setSuccessMessage("Files uploaded successfully");
    } catch (err) {
      console.error("Error uploading files:", err);
      setError(err instanceof Error ? err.message : "Failed to upload files");
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const handleDelete = async (upload: Upload) => {
    if (
      !confirm(
        "Are you sure you want to delete this file? This action cannot be undone.",
      )
    )
      return;

    try {
      // Delete from storage
      const filePath = upload.url.split("/uploads/")[1];
      if (!filePath) throw new Error("Invalid file path");

      const { error: storageError } = await supabase.storage
        .from("uploads")
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("uploads")
        .update({ deleted: true })
        .eq("id", upload.id);

      if (dbError) throw dbError;

      // Refresh data
      await fetchUploads();
      setSuccessMessage("File deleted successfully");
    } catch (err) {
      console.error("Error deleting file:", err);
      setError("Failed to delete file");
    }
  };

  const handleReplaceClick = (upload: Upload) => {
    setShowReplaceConfirm(upload);
  };

  const handleReplaceConfirm = async () => {
    if (!showReplaceConfirm) return;

    try {
      setUploading(true);
      setError(null);

      // Create a file input element
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf,.csv,.xlsx";

      // Handle file selection
      fileInput.onchange = async (e) => {
        const target = e.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) {
          setUploading(false);
          return;
        }

        const file = target.files[0];

        // Check file size
        if (file.size > 50 * 1024 * 1024) {
          // 50MB
          setError(`File ${file.name} exceeds maximum size of 50MB`);
          setUploading(false);
          return;
        }

        try {
          // Extract the file path from the URL
          const filePath = showReplaceConfirm.url.split("/uploads/")[1];
          if (!filePath) {
            throw new Error("Invalid file path");
          }

          // Upload the new file with the same path (replacing the old one)
          const { error: uploadError } = await supabase.storage
            .from("uploads")
            .upload(filePath, file, {
              cacheControl: "3600",
              upsert: true, // Use upsert to replace existing file
            });

          if (uploadError) throw uploadError;

          // Update the database record with new file info
          const { error: updateError } = await supabase
            .from("uploads")
            .update({
              name: showReplaceConfirm.name, // Keep the same name
              size: file.size,
              file_type: file.type,
              updated_at: new Date().toISOString(),
            })
            .eq("id", showReplaceConfirm.id);

          if (updateError) throw updateError;

          setSuccessMessage(
            `File "${showReplaceConfirm.name}" replaced successfully`,
          );
          await fetchUploads();
        } catch (err) {
          console.error("Error replacing file:", err);
          setError(
            err instanceof Error ? err.message : "Failed to replace file",
          );
        } finally {
          setUploading(false);
          setShowReplaceConfirm(null);
        }
      };

      // Trigger file selection
      fileInput.click();
    } catch (err) {
      console.error("Error initiating file replacement:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to initiate file replacement",
      );
      setUploading(false);
      setShowReplaceConfirm(null);
    }
  };

  const handleCopyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Uploads"
        description="Admin interface for managing document uploads in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">PDF Uploads</h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div
        {...getRootProps()}
        className={`mb-8 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-[#F98B3D] bg-orange-50"
            : "border-gray-300 hover:border-[#F98B3D]"
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex justify-center mb-4">
          <FileText className="h-12 w-12 text-gray-400 mx-2" />
          <FileSpreadsheet className="h-12 w-12 text-gray-400 mx-2" />
        </div>
        <p className="text-gray-600">
          {isDragActive
            ? "Drop the files here..."
            : "Drag & drop PDF or CSV files here, or click to select files"}
        </p>
        <p className="text-sm text-gray-500 mt-2">Maximum file size: 50MB</p>
      </div>

      {uploading && (
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <Loader className="h-5 w-5 text-blue-400 animate-spin" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">Uploading files...</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {uploads.map((upload) => (
            <li key={upload.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {upload.file_type.includes("pdf") ? (
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    ) : upload.file_type.includes("csv") ||
                      upload.file_type.includes("excel") ||
                      upload.file_type.includes("spreadsheet") ? (
                      <FileSpreadsheet className="h-5 w-5 text-gray-400 mr-3" />
                    ) : (
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    )}
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {upload.name}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCopyUrl(upload.url, upload.id)}
                      className="text-gray-400 hover:text-gray-500"
                      title="Copy URL"
                    >
                      {copied === upload.id ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleReplaceClick(upload)}
                      className="text-gray-400 hover:text-gray-500"
                      title="Replace file"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z" />
                      </svg>
                    </button>
                    <a
                      href={upload.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-500"
                      title="View"
                    >
                      <Download className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => handleDelete(upload)}
                      className="text-red-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500">
                      {formatFileSize(upload.size)}
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                      {upload.file_type}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                    <p>
                      Uploaded on{" "}
                      {new Date(upload.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {uploads.length === 0 && (
            <li>
              <div className="px-4 py-12 text-center">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-gray-400 mx-2" />
                  <FileSpreadsheet className="h-12 w-12 text-gray-400 mx-2" />
                </div>
                <p className="text-gray-500">No files uploaded yet</p>
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* Replace Confirmation Modal */}
      {showReplaceConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Replace File
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to replace "{showReplaceConfirm.name}"? This
              will keep the same URL but update the file content.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowReplaceConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReplaceConfirm}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
              >
                Replace File
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
